import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconTrash, IconRefresh, IconChevronRight, IconChevronDown, IconPencil, IconDownload } from "@tabler/icons-react";
import { listRuns, deleteRun, listExperiments, deleteExperiment, updateExperiment, EXPORT_PATHS, downloadExport } from "../services/api";
import VendorBadge, { vendorOfSystem, vendorOfModel } from "../components/VendorBadge";
import CommitLink from "../components/CommitLink";
import { promptsStatusChip } from "../promptsCommit";
import RedFlag from "../components/RedFlag";
import ConsistencyCell, { consistencyRank } from "../components/ConsistencyCell";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// Rubric-capture score chip: N/M (X%), colored by %. Dash when ungraded.
const pctChip = (captured, total, pct) => {
  if (total == null || pct == null) return <span className="muted">—</span>;
  const cls = pct >= 80 ? "ok" : pct >= 50 ? "warn" : "bad";
  return <span className={`chip ${cls}`}>{captured}/{total} ({pct}%)</span>;
};

const TIER_CLS = { TOP1: "ok", TOP3: "ok", TOP5: "warn", DNM: "n", MISS: "bad" };
const tierChip = (tier) =>
  tier ? <span className={`chip ${TIER_CLS[tier] || "n"}`}>{tier}</span> : <span className="muted">—</span>;

const userLocal = (email) => (email ? email.replace(/@.*/, "") : "—");

// --- aggregate formatting (server stats: {mean, sd, n}) ---
const rate = (x) => (x == null ? "—" : Math.round(x * 100) + "%");
const fmtScore = (s) => (!s || s.mean == null ? "—" : s.sd == null ? `${s.mean}%` : `${s.mean}% ± ${s.sd}`);
const fmtNum = (s) => (!s || s.mean == null ? "—" : s.sd == null ? `${s.mean}` : `${s.mean} ± ${s.sd}`);
// SD reported as a % of the mean (coefficient of variation) — easier to eyeball variance across metrics.
const cvSuffix = (s) => (!s || s.mean == null || s.sd == null || s.mean === 0 ? "" : ` ± ${Math.round((100 * s.sd) / s.mean)}%`);
const fmtDurCV = (s) => (!s || s.mean == null ? "—" : `${(s.mean / 1000).toFixed(1)}s${cvSuffix(s)}`);

// per-run durations (seconds): SUT-gen total per case + latency per question.
const fmtSec = (ms) => (ms == null ? "—" : `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`);
const latencyPerTurn = (r) => (r.sut_gen_ms == null || !r.n_turns ? null : r.sut_gen_ms / r.n_turns);

// Persona band chip (NAAL literacy bands — distinct hues, deliberately NOT good/bad colors).
const PERSONA_CHIP = {
  baseline: { label: "BASE", bg: "#e8eefc", fg: "#3552a0" },
  lower: { label: "LOWER", bg: "#fdf0e0", fg: "#a5661a" },
  upper: { label: "UPPER", bg: "#e4f3ee", fg: "#1c7a63" },
};
const personaChip = (style) => {
  const p = PERSONA_CHIP[style];
  if (!p) return <span className="muted">{style || "—"}</span>;
  return (
    <span style={{ background: p.bg, color: p.fg, fontSize: 10, fontWeight: 700,
      padding: "1px 6px", borderRadius: 4, letterSpacing: ".3px" }}>{p.label}</span>
  );
};

// DX tier DISTRIBUTION (non-cumulative %, sums to 100% over graded runs). MISS is flagged red when >0.
function DxCell({ dx }) {
  if (!dx || !dx.n) return <span className="muted">—</span>;
  const missRed = (dx.miss || 0) > 0;
  return (
    <>
      <div style={{ fontSize: 11 }}>T1 {rate(dx.top1)} · T3 {rate(dx.top3)} · T5 {rate(dx.top5)}</div>
      <div className="muted" style={{ fontSize: 11 }}>
        DNM {rate(dx.dnm)} · <span style={missRed ? { color: "var(--bad)", fontWeight: 700 } : undefined}>MISS {rate(dx.miss)}</span>
      </div>
    </>
  );
}

// --- SUT ranking for the summary table: a single-hue ramp, hekavor purple (best) -> pale lavender
// (worst), evenly stepped for however many SUTs are being compared. ---
const RANK_BEST = [81, 28, 205];      // #511ccd (hekavor purple / --accent)
const RANK_WORST = [220, 212, 248];   // pale lavender — wider span = more separation between ranks
const rankGradient = `linear-gradient(90deg, rgb(${RANK_BEST.join(",")}), rgb(${RANK_WORST.join(",")}))`;
function rankPalette(n) {
  if (n <= 1) return [`rgb(${RANK_BEST.join(",")})`];
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);   // 0 = best (full purple), 1 = worst (lightest)
    return `rgb(${RANK_BEST.map((c, k) => lerp(c, RANK_WORST[k], t)).join(",")})`;
  });
}
// Weighted DX score (higher is better): top1×4 + top3×3 + top5×2 + dnm×1 + miss×0.
const dxWeighted = (dx) => (!dx || !dx.n ? null : 4 * (dx.top1 || 0) + 3 * (dx.top3 || 0) + 2 * (dx.top5 || 0) + 1 * (dx.dnm || 0));
// `m.capture_score || m.score`: pre-split experiments only have the legacy combined `score` stat, so
// fall it back into the Must-Capture column (Red Flags stays "—" for those old experiments).
const RANK_METRICS = [
  { key: "redflag_score", get: (m) => consistencyRank(m.redflag_consistency) ?? (m.redflag_score && m.redflag_score.mean), dir: "high" },
  { key: "capture_score", get: (m) => consistencyRank(m.capture_consistency) ?? ((m.capture_score || m.score) && (m.capture_score || m.score).mean), dir: "high" },
  { key: "dx", get: (m) => dxWeighted(m.dx), dir: "high" },
  { key: "turns", get: (m) => m.turns && m.turns.mean, dir: "low" },   // fewer turns = better
  { key: "interview_ms", get: (m) => m.interview_ms && m.interview_ms.mean, dir: "low" },
  { key: "sut_gen_ms", get: (m) => m.sut_gen_ms && m.sut_gen_ms.mean, dir: "low" },
  { key: "sut_gen_per_turn_ms", get: (m) => m.sut_gen_per_turn_ms && m.sut_gen_per_turn_ms.mean, dir: "low" },
];
// For the SUT rows, return [{metricKey: color}] per row. Skips a metric unless ≥2 SUTs have a value.
function computeRanks(rows) {
  const perRow = rows.map(() => ({}));
  for (const spec of RANK_METRICS) {
    const vals = rows.map((m, i) => ({ v: spec.get(m), i })).filter((x) => x.v != null && !Number.isNaN(x.v));
    if (vals.length < 2) continue;
    const palette = rankPalette(vals.length);
    vals.sort((a, b) => (spec.dir === "high" ? b.v - a.v : a.v - b.v));
    let rank = 0;
    vals.forEach((x, j) => {
      if (j > 0 && x.v !== vals[j - 1].v) rank = j;   // competition ranking (ties share a color)
      perRow[x.i][spec.key] = palette[Math.min(rank, palette.length - 1)];
    });
  }
  return perRow;
}
const rankBar = (color) => (color ? <span className="rankbar" style={{ background: color }} /> : null);
const rCell = (color, content) => (
  <td><span className="rankcell">{rankBar(color)}
    <span style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>{content}</span>
  </span></td>
);

// Shared metric cells. `ranks` (a {metricKey: color} map) is passed only for the per-SUT rows, so the
// rank strips show up there and NOT on the per-persona sub-rows. Runs is a count, never ranked.
const MetricCells = ({ m, ranks }) => {
  const r = ranks || {};
  return (
    <>
      <td>{m.n_complete}/{m.n}</td>
      {rCell(r.redflag_score, <ConsistencyCell c={m.redflag_consistency} legacyText={fmtScore(m.redflag_score)} />)}
      {rCell(r.capture_score, <ConsistencyCell c={m.capture_consistency} legacyText={fmtScore(m.capture_score || m.score)} />)}
      {rCell(r.dx, <DxCell dx={m.dx} />)}
      {rCell(r.turns, fmtNum(m.turns))}
      {rCell(r.interview_ms, fmtDurCV(m.interview_ms))}
      {rCell(r.sut_gen_ms, fmtDurCV(m.sut_gen_ms))}
      {rCell(r.sut_gen_per_turn_ms, fmtDurCV(m.sut_gen_per_turn_ms))}
    </>
  );
};

// Per-SUT aggregate summary — always shown on an experiment card. Each SUT row expands to its
// per-persona sub-rows (BASE/LOWER/UPPER) when it ran more than one persona.
function PerModelTable({ perModel }) {
  const [open, setOpen] = useState(() => new Set());
  if (!perModel || perModel.length === 0) return <div className="muted" style={{ fontSize: 12 }}>No recorded runs.</div>;
  const toggle = (sys) => setOpen((s) => { const n = new Set(s); n.has(sys) ? n.delete(sys) : n.add(sys); return n; });
  const ranks = computeRanks(perModel);   // per-SUT rank colors, one metric map per row
  return (
    <table>
      <thead><tr>
        <th>SUT</th><th>Runs</th>
        <th style={{ whiteSpace: "nowrap" }}><RedFlag /> Red Flags</th><th>Must Capture</th>
        <th>DX</th><th>Turns</th><th>Interview</th><th>SUT gen</th><th>Avg. Latency</th>
      </tr></thead>
      <tbody>
        {perModel.map((m, idx) => {
          const expandable = (m.per_persona || []).length > 1;
          const isOpen = open.has(m.system);
          return (
            <React.Fragment key={m.system}>
              <tr onClick={() => expandable && toggle(m.system)} style={{ cursor: expandable ? "pointer" : "default" }}>
                <td><span className="flex" style={{ gap: 6 }}>
                  {expandable && (isOpen ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />)}
                  <VendorBadge vendor={vendorOfSystem(m.system)} />
                </span></td>
                <MetricCells m={m} ranks={ranks[idx]} />
              </tr>
              {isOpen && m.per_persona.map((p) => (
                <tr key={m.system + p.persona} style={{ background: "var(--bg)" }}>
                  <td style={{ paddingLeft: 30 }}>{personaChip(p.persona)}</td>
                  <MetricCells m={p} />
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export default function RunsStorage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);              // { experiments, ungrouped }
  const [expanded, setExpanded] = useState(() => new Set());
  const [runsByKey, setRunsByKey] = useState({});      // exp key -> runs[]
  const [selected, setSelected] = useState(() => new Set());       // run ids
  const [selectedExps, setSelectedExps] = useState(() => new Set()); // experiment ids
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState("");
  const [ctrl, setCtrl] = useState({});   // exp key -> { group, fSystem, fTargetDx, fCommStyle }
  const [renaming, setRenaming] = useState(null);   // { id, name } while the rename modal is open
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameErr, setRenameErr] = useState("");

  const load = () => listExperiments().then(setData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const keyOf = (exp) => exp.id || "__ungrouped__";

  const fetchRuns = async (exp) => {
    const d = await listRuns(exp.id ? { experiment_id: exp.id } : {});
    const rs = exp.id ? d.runs : d.runs.filter((r) => !r.experiment_id);
    setRunsByKey((prev) => ({ ...prev, [keyOf(exp)]: rs }));
  };

  const toggle = async (exp) => {
    const k = keyOf(exp);
    const willOpen = !expanded.has(k);
    setExpanded((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
    if (willOpen && !runsByKey[k]) await fetchRuns(exp);
  };

  const open = (id) => nav(`/runs/${id}`);            // clicking a run opens the dedicated detail page

  // After a mutation, reload the aggregates + refetch any expanded groups' runs.
  const refresh = async () => {
    const exps = await listExperiments();
    setData(exps);
    const groups = [...(exps.experiments || []), exps.ungrouped].filter(Boolean);
    const updates = {};
    for (const exp of groups) {
      const k = keyOf(exp);
      if (expanded.has(k)) {
        const d = await listRuns(exp.id ? { experiment_id: exp.id } : {});
        updates[k] = exp.id ? d.runs : d.runs.filter((r) => !r.experiment_id);
      }
    }
    setRunsByKey(updates);
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExpSel = (id) => setSelectedExps((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selCount = selectedExps.size + selected.size;

  const doRename = async () => {
    const trimmed = (renaming.name || "").trim();
    if (!trimmed) return;
    setRenameBusy(true); setRenameErr("");
    try {
      await updateExperiment(renaming.id, trimmed);
      setRenaming(null);
      await load();
    } catch (e) {
      setRenameErr(e?.response?.data?.detail || "Rename failed.");
    }
    setRenameBusy(false);
  };

  // Delete selected experiments (each removes ALL its runs) + selected individual runs. A run that lived
  // in a deleted experiment 404s on its own delete — Promise.allSettled tolerates that.
  const confirmBulkDelete = async () => {
    setDeleting(true); setDelErr("");
    const expIds = [...selectedExps];
    const runIds = [...selected];
    const res = await Promise.allSettled([
      ...expIds.map((id) => deleteExperiment(id)),
      ...runIds.map((id) => deleteRun(id).catch((e) => { if (e?.response?.status !== 404) throw e; })),
    ]);
    const failed = res.filter((r) => r.status === "rejected").length;
    setSelected(new Set());
    setSelectedExps(new Set());
    await refresh();
    setDeleting(false);
    if (failed) setDelErr(`${failed} deletion(s) failed.`);
    else setConfirmBulk(false);
  };

  // --- per-experiment run table: filter + group/sort controls (shown when expanded) ---
  const COLS = 10;
  const GROUP_OPTS = [
    { key: "", label: "No grouping" },
    { key: "system", label: "SUT" },
    { key: "target_dx", label: "Target DX" },
    { key: "comm_style", label: "Persona" },
  ];
  const ctrlOf = (k) => ctrl[k] || { group: "", fSystem: "", fTargetDx: "", fCommStyle: "" };
  const setCtrlField = (k, field, val) => setCtrl((c) => ({ ...c, [k]: { ...ctrlOf(k), [field]: val } }));

  const runRow = (r) => (
    <tr key={r.id} onClick={() => open(r.id)} title={r.id}
        style={{ cursor: "pointer" }} className={selected.has(r.id) ? "row-sel" : undefined}>
      <td onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        <span className="whenrow">
          <span className="status-dot" title={r.status} style={{ background: r.status === "complete" ? "var(--ok)" : "var(--bad)" }} />
          <span className="when">{fmtWhen(r.created_at)}</span>
        </span>
      </td>
      <td>
        <div>{r.chief_complaint || "—"}</div>
        <div className="muted" style={{ fontSize: 11 }}>{r.target_dx ? `→ ${r.target_dx}` : "—"}</div>
      </td>
      <td><VendorBadge vendor={vendorOfSystem(r.system)} label={false} /></td>
      <td><span className="flex" style={{ gap: 6 }}>
        <VendorBadge vendor={vendorOfModel(r.emulator_model)} label={false} />{personaChip(r.comm_style)}
      </span></td>
      <td>{pctChip(r.redflag_captured, r.redflag_total, r.redflag_score_pct)}</td>
      <td>{pctChip(r.capture_captured ?? r.eval_captured, r.capture_total ?? r.eval_total, r.capture_score_pct ?? r.eval_score_pct)}</td>
      <td>{tierChip(r.dx_tier)}</td>
      <td>{r.n_turns}</td>
      <td>{fmtSec(r.sut_gen_ms)}</td>
      <td>{fmtSec(latencyPerTurn(r))}</td>
    </tr>
  );

  const groupHeaderRow = (key, val, count) => (
    <tr style={{ background: "var(--bg)" }}>
      <td colSpan={COLS} style={{ fontWeight: 600, fontSize: 12 }}>
        <span className="flex" style={{ gap: 8 }}>
          {key === "system" ? <VendorBadge vendor={vendorOfSystem(val)} />
            : key === "comm_style" ? personaChip(val)
              : (val || "—")}
          <span className="muted">({count})</span>
        </span>
      </td>
    </tr>
  );

  const renderExpanded = (k, runs) => {
    if (!runs) return <div className="muted" style={{ padding: 6 }}>Loading runs…</div>;
    const c = ctrlOf(k);
    const distinct = (key) => [...new Set(runs.map((r) => r[key]).filter((v) => v != null))].sort();
    const filtered = runs.filter((r) =>
      (!c.fSystem || r.system === c.fSystem) &&
      (!c.fTargetDx || r.target_dx === c.fTargetDx) &&
      (!c.fCommStyle || r.comm_style === c.fCommStyle));
    // grouping also sorts by the group key; ungrouped keeps the default newest-first order.
    const groups = c.group
      ? [...filtered.reduce((m, r) => m.set(r[c.group] ?? "—", [...(m.get(r[c.group] ?? "—") || []), r]), new Map())
          .entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      : [[null, filtered]];

    const filterSel = (field, label) => (
      <label className="flex" style={{ gap: 4 }}>
        <span className="muted">{label}</span>
        <select value={c[field] || ""} onChange={(e) => setCtrlField(k, field, e.target.value)}>
          <option value="">All</option>
          {distinct(field === "fSystem" ? "system" : field === "fTargetDx" ? "target_dx" : "comm_style")
            .map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );

    return (
      <>
        <div className="flex" style={{ gap: 14, marginBottom: 8, flexWrap: "wrap", fontSize: 12 }}>
          <label className="flex" style={{ gap: 4 }}>
            <span className="muted">Group / sort</span>
            <select value={c.group || ""} onChange={(e) => setCtrlField(k, "group", e.target.value)}>
              {GROUP_OPTS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </label>
          {filterSel("fSystem", "SUT")}
          {filterSel("fTargetDx", "Target DX")}
          {filterSel("fCommStyle", "Persona")}
          <div className="spacer" />
          <span className="muted">{filtered.length} run{filtered.length === 1 ? "" : "s"}</span>
        </div>
        <table>
          <thead><tr>
            <th style={{ width: 28 }}></th><th>When / Status</th><th>Case</th><th>Hist. tool</th>
            <th>EMU / Persona</th><th style={{ whiteSpace: "nowrap" }}><RedFlag /> Red Flags</th><th>Must Capture</th>
            <th>DX</th><th>Turns</th><th>SUT gen</th><th>Avg. Latency</th>
          </tr></thead>
          <tbody>
            {groups.map(([val, rs]) => (
              <React.Fragment key={val ?? "__all__"}>
                {c.group && groupHeaderRow(c.group, val, rs.length)}
                {rs.map(runRow)}
              </React.Fragment>
            ))}
            {filtered.length === 0 && <tr><td colSpan={COLS} className="muted" style={{ padding: 12 }}>No runs.</td></tr>}
          </tbody>
        </table>
      </>
    );
  };

  if (!data) return <div className="page-head"><h2>Results</h2></div>;

  const groups = [...(data.experiments || [])];
  if (data.ungrouped && data.ungrouped.n_runs > 0) groups.push(data.ungrouped);

  return (
    <>
      <div className="page-head"><h2>Results</h2></div>
      <div className="flex" style={{ marginBottom: 12, gap: 8 }}>
        <button className="btn sm" onClick={load}><IconRefresh size={14} /> Refresh</button>
        <div className="spacer" />
        <span className="muted">{groups.length} experiment{groups.length === 1 ? "" : "s"}</span>
        <button className="btn sm" disabled={selCount === 0}
          style={selCount ? { borderColor: "var(--bad)", color: "var(--bad)" } : undefined}
          onClick={() => { setDelErr(""); setConfirmBulk(true); }}>
          <IconTrash size={14} /> Delete selected ({selCount})
        </button>
      </div>

      {groups.length === 0 && (
        <div className="card"><span className="muted">No runs yet — launch one in Experiment.</span></div>
      )}

      {groups.map((exp) => {
        const k = keyOf(exp);
        const isOpen = expanded.has(k);
        const runs = runsByKey[k];
        return (
          <div className="card" key={k} style={{ padding: 0, overflow: "hidden" }}>
            <div className="between" style={{ padding: "12px 16px" }}>
              <div className="flex" style={{ gap: 8 }}>
                {exp.id && (
                  <input type="checkbox" title="Select experiment (delete removes all its runs)"
                    checked={selectedExps.has(exp.id)}
                    onChange={() => toggleExpSel(exp.id)} />
                )}
                <b>{exp.name}</b>
                {exp.id && (
                  <button title="Rename experiment"
                    onClick={() => { setRenameErr(""); setRenaming({ id: exp.id, name: exp.name }); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2, display: "inline-flex" }}>
                    <IconPencil size={13} />
                  </button>
                )}
                {exp.created_at && <span className="when">{fmtWhen(exp.created_at)}</span>}
                <span className="muted" style={{ fontSize: 12 }}>{exp.n_complete}/{exp.n_runs} runs</span>
                {exp.service_type && <span className="chip" title="Mari mode (clinic_service_type)">{exp.service_type}</span>}
                {exp.model_set === "previous" && <span className="chip" title="Study model set (history SUTs + emulator; Mari + evaluator stay current)">previous models</span>}
                {(() => {
                  const st = promptsStatusChip(exp);
                  if (st) return <span className={`chip ${st.tone}`} title={st.title}>{st.label}</span>;
                  return exp.prompts_commit ? (
                    <span className="chip n" title={`Mari runs pinned to florence-prompts ${exp.prompts_commit}`}>
                      prompts <CommitLink sha={exp.prompts_commit} icon={false} /></span>
                  ) : null;
                })()}
                {exp.created_by && <span className="when" title={exp.created_by}>{userLocal(exp.created_by)}</span>}
              </div>
              <div className="flex" style={{ gap: 12 }}>
                {exp.id && (
                  <span className="flex" style={{ gap: 6 }}>
                    <button className="btn sm" title="Per-run CSV — case context + scores (Excel)"
                            onClick={() => downloadExport(EXPORT_PATHS.experimentRuns(exp.id), `${exp.name}_runs.csv`)}><IconDownload size={13} /> Runs CSV</button>
                    <button className="btn sm" title="Aggregated stats CSV — per SUT/persona"
                            onClick={() => downloadExport(EXPORT_PATHS.experimentStats(exp.id), `${exp.name}_stats.csv`)}><IconDownload size={13} /> Stats CSV</button>
                  </span>
                )}
                {(exp.per_model || []).length > 1 && (
                  <span className="flex" style={{ gap: 5, fontSize: 10.5, color: "var(--muted)", whiteSpace: "nowrap" }}
                    title="Purple bars rank each metric across the SUTs — darker is better.">
                    best<span style={{ width: 42, height: 8, borderRadius: 2, background: rankGradient }} />worst
                  </span>
                )}
                <span className="flex" style={{ gap: 6 }}>
                  {(exp.systems || []).map((s) => <VendorBadge key={s} vendor={vendorOfSystem(s)} label={false} />)}
                </span>
              </div>
            </div>

            <div style={{ padding: "0 16px 14px", overflowX: "auto" }}>
              <PerModelTable perModel={exp.per_model} />
            </div>

            {exp.n_runs > 0 && (
              <div onClick={() => toggle(exp)} title={isOpen ? "Hide runs" : "See all runs"}
                style={{ borderTop: "1px solid var(--line)", padding: "9px 16px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
                  color: "var(--accent)", userSelect: "none" }}>
                {isOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                {isOpen ? "Hide runs" : `See all ${exp.n_runs} run${exp.n_runs === 1 ? "" : "s"}`}
              </div>
            )}
            {isOpen && (
              <div style={{ padding: "4px 16px 14px", overflowX: "auto" }}>
                {renderExpanded(k, runs)}
              </div>
            )}
          </div>
        );
      })}

      {confirmBulk && (
        <div className="modal-overlay" onClick={() => !deleting && setConfirmBulk(false)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <h3>Delete selected?</h3>
            <p className="muted" style={{ marginTop: 4 }}>
              Permanently delete{" "}
              {selectedExps.size > 0 && (
                <b>{selectedExps.size} experiment{selectedExps.size === 1 ? "" : "s"} (and all their runs)</b>
              )}
              {selectedExps.size > 0 && selected.size > 0 && " and "}
              {selected.size > 0 && (
                <b>{selected.size} individual run{selected.size === 1 ? "" : "s"}</b>
              )}
              , including transcripts. This can&apos;t be undone.
            </p>
            {delErr && <div className="banner bad" style={{ marginTop: 8 }}>{delErr}</div>}
            <div className="flex" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="btn sm" disabled={deleting} onClick={() => setConfirmBulk(false)}>Cancel</button>
              <button className="btn sm" disabled={deleting}
                style={{ borderColor: "var(--bad)", color: "var(--bad)" }} onClick={confirmBulkDelete}>
                {deleting ? "Deleting…" : `Delete ${selCount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {renaming && (
        <div className="modal-overlay" onClick={() => !renameBusy && setRenaming(null)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <h3>Rename experiment</h3>
            <input type="text" value={renaming.name} autoFocus
              onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") doRename(); }}
              style={{ width: "100%", marginTop: 10 }} />
            {renameErr && <div className="banner bad" style={{ marginTop: 8 }}>{renameErr}</div>}
            <div className="flex" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="btn sm" disabled={renameBusy} onClick={() => setRenaming(null)}>Cancel</button>
              <button className="btn sm primary" disabled={renameBusy || !renaming.name.trim()} onClick={doRename}>
                {renameBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
